// apps/web/src/features/cargo/components/CargoRegistryTable.tsx
//
// Dense wide registry table (#1231). Native `<table>` with two sticky-left
// columns (index + article) and horizontal + vertical scroll inside the table
// viewport. Row hover tints the whole row INCLUDING the sticky cells (the
// sticky cells inherit the hovered background via a CSS rule, since a sticky
// cell paints its own background over the row tint).

import { useTranslation } from 'react-i18next';
import type { CSSProperties } from 'react';
import type { CargoArticleWire } from '@magimp/shared';

import {
  cargoColumnHeaderKey,
  type CargoCellContext,
  type CargoColumn,
  type CargoColumnKey,
} from './cargoColumns.js';
import type { CargoRowActions } from './cargoActionCells.js';

const STICKY_INDEX_LEFT = 0;
// `#` column width; the article column sticks immediately to its right.
const INDEX_COL_WIDTH = 48;

// 3-line clamp for long display text. Lives on an inner wrapper, never the
// <td>, so the cell keeps native table-cell layout.
const CLAMP_STYLE: CSSProperties = {
  display: '-webkit-box',
  WebkitBoxOrient: 'vertical',
  WebkitLineClamp: 3,
  overflow: 'hidden',
  overflowWrap: 'anywhere',
};

// Columns that render inline interactive controls (#1232) wider than their
// dense display width. With actions present they keep only a min-width floor so
// the control is never clipped; without actions they stay hard-fixed dense.
const FLEXIBLE_COLUMN_KEYS = new Set<CargoColumnKey>([
  'transportationTariffForClients',
  'clientPaymentStatus',
]);

function alignToText(align: CargoColumn['align']): 'left' | 'right' | 'center' {
  if (align === 'end') return 'right';
  if (align === 'center') return 'center';
  return 'left';
}

export function CargoRegistryTable({
  rows,
  columns,
  locale,
  actions,
  onRowClick,
}: {
  rows: readonly CargoArticleWire[];
  columns: readonly CargoColumn[];
  locale: string;
  /** Interactive row controls (#1232). Absent ⇒ a read-only registry. */
  actions?: CargoRowActions | undefined;
  /** Row-body click (opens the role-appropriate cargo card). */
  onRowClick?: ((row: CargoArticleWire) => void) | undefined;
}) {
  const { t } = useTranslation(['cargo']);
  const ctx: CargoCellContext = actions ? { t, locale, actions } : { t, locale };

  return (
    <div
      data-testid="cargo-registry-table-scroll"
      style={{ height: '100%', overflow: 'auto', background: 'var(--bg-0)' }}
    >
      <style>{stickyHoverCss}</style>
      <table
        data-testid="cargo-registry-table"
        style={{
          borderCollapse: 'separate',
          borderSpacing: 0,
          fontSize: 'var(--fz-11)',
          color: 'var(--fg-1)',
          width: 'max-content',
          minWidth: '100%',
        }}
      >
        <thead>
          <tr>
            {columns.map((col, colIndex) => {
              const colWidth = col.key === 'index' ? INDEX_COL_WIDTH : col.width;
              const flexible = actions !== undefined && FLEXIBLE_COLUMN_KEYS.has(col.key);
              const widthStyle: CSSProperties = flexible
                ? { minWidth: colWidth }
                : { width: colWidth, minWidth: colWidth, maxWidth: colWidth };
              return (
                <th
                  key={col.key}
                  data-col={col.key}
                  style={{
                    position: 'sticky',
                    top: 0,
                    zIndex: col.sticky ? 4 : 2,
                    ...stickyLeftStyle(col, colIndex, columns),
                    background: 'var(--bg-2)',
                    color: 'var(--fg-2)',
                    fontWeight: 600,
                    textAlign: alignToText(col.align),
                    padding: '5px 6px',
                    borderBottom: '1px solid var(--border-strong)',
                    borderRight: '1px solid var(--border-1)',
                    whiteSpace: 'normal',
                    lineHeight: 'var(--lh-tight)',
                    ...widthStyle,
                    verticalAlign: 'bottom',
                  }}
                >
                  {col.key === 'index' ? '#' : t(cargoColumnHeaderKey(col.key))}
                </th>
              );
            })}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, rowIndex) => (
            <tr
              key={row.id}
              className="cargo-row"
              data-testid="cargo-registry-row"
              onClick={onRowClick ? () => onRowClick(row) : undefined}
              style={onRowClick ? { cursor: 'pointer' } : undefined}
            >
              {columns.map((col, colIndex) => {
                const colWidth = col.key === 'index' ? INDEX_COL_WIDTH : col.width;
                const node = col.render(row, ctx, rowIndex + 1);
                const isText = typeof node === 'string';
                const clamp = isText && col.align !== 'end' && col.key !== 'delete';
                const flexible = actions !== undefined && FLEXIBLE_COLUMN_KEYS.has(col.key);
                const widthStyle: CSSProperties = flexible
                  ? { minWidth: colWidth }
                  : { width: colWidth, minWidth: colWidth, maxWidth: colWidth };
                return (
                  <td
                    key={col.key}
                    data-col={col.key}
                    className={col.sticky ? 'cargo-sticky-cell' : undefined}
                    style={{
                      position: col.sticky ? 'sticky' : undefined,
                      zIndex: col.sticky ? 1 : undefined,
                      ...stickyLeftStyle(col, colIndex, columns),
                      background: 'var(--bg-0)',
                      textAlign: alignToText(col.align),
                      padding: '4px 6px',
                      borderBottom: '1px solid var(--border-1)',
                      borderRight: '1px solid var(--border-1)',
                      ...widthStyle,
                      overflow: isText ? 'hidden' : 'visible',
                      whiteSpace: isText && !clamp ? 'nowrap' : undefined,
                      textOverflow: isText && !clamp ? 'ellipsis' : undefined,
                      fontVariantNumeric: col.align === 'end' ? 'tabular-nums' : undefined,
                    }}
                    title={isText ? node : undefined}
                  >
                    {clamp ? <div style={CLAMP_STYLE}>{node}</div> : node}
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// Left offset for the two sticky columns: `#` at 0, article after the index.
function stickyLeftStyle(
  col: CargoColumn,
  colIndex: number,
  columns: readonly CargoColumn[],
): { left?: number } {
  if (!col.sticky) return {};
  if (colIndex === 0) return { left: STICKY_INDEX_LEFT };
  // Article column sticks right after the index column.
  const indexIsSticky = columns[0]?.sticky;
  return { left: indexIsSticky ? INDEX_COL_WIDTH : 0 };
}

// Sticky cells paint their own background over the row tint, so the hover
// background must be re-applied to them via a sibling-aware rule.
const stickyHoverCss = `
.cargo-row:hover > td { background: var(--bg-1) !important; }
.cargo-row:hover > td.cargo-sticky-cell { background: var(--bg-1) !important; }
`;
